// ------------------------------
// src/components/organisms/users/TCModal.jsx
// ------------------------------
import CustomButton from 'components/atoms/CustomButton';
import ModalUser from 'components/molecules/user/ModalUser';
import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import { Spin, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';

const styles = {
  title: 'text-black font-normal text-2xl',
  sub_title: 'text-base font-semibold',
  paragraph: 'list-disc list-inside',
};

/**
 * Format a timestamp as DD/MM/YYYY without usare librerie esterne.
 */
const formatDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Modal PP + T&C + Marketing‑consent checkbox.
 *
 * Props
 *  - show / onClose: gestione visibilità
 *  - onSubmit      : accetta PP + T&C
 *  - onLogout      : logout
 *  - isLoading     : loading globale bottoni accetta / logout
 *  - pp, tc        : { data: [...], updated_at }
 *  - isFetching    : true finché carichi PP + T&C
 *  - initialMarketingAccepted : boolean (stato corrente da GET /marketing-users)
 *  - isFetchingMarketing      : boolean (loading checkbox iniziale)
 *  - onToggleMarketing(next)  : async → PUT /marketing-users/accept
 */
export default function TCModal({
                                  show,
                                  onClose,
                                  onSubmit,
                                  onLogout,
                                  isLoading,
                                  pp,
                                  tc,
                                  isFetching,
                                  /* marketing */
                                  initialMarketingAccepted = false,
                                  isFetchingMarketing,
                                  onToggleMarketing,
                                }) {
  const { t } = useTranslation();

  /* --------------------------------------------------
   *   Checkbox stato  → sincr. con prop ogni volta
   * -------------------------------------------------- */
  const [marketingAccepted, setMarketingAccepted] = useState(initialMarketingAccepted);
  const [savingMarketing, setSavingMarketing] = useState(false);

  useEffect(() => {
    setMarketingAccepted(initialMarketingAccepted);
  }, [initialMarketingAccepted]);

  const handleMarketingChange = async (e) => {
    const next = e.target.checked;
    setMarketingAccepted(next);
    setSavingMarketing(true);
    try {
      await onToggleMarketing(next);
    } catch (_) {
      // rollback UI se errore
      setMarketingAccepted(!next);
    } finally {
      setSavingMarketing(false);
    }
  };

  /* --------------------------------------------------
   *   Render blocchi PP / TC
   * -------------------------------------------------- */
  const renderBlocks = (arr, updatedAt, prefix) =>
    arr?.map(({ key, value }, idx) => {
      switch (key) {
        case 'title':
          return (
            <span key={`${prefix}-title-${idx}`} className={styles[key]}>
              {value} ({t('last updated')}: {formatDate(updatedAt)})
            </span>
          );
        case 'sub_title':
          return (
            <span key={`${prefix}-sub-${idx}`} className={styles[key]}>
              {value}
            </span>
          );
        case 'paragraph':
          return <div key={`${prefix}-p-${idx}`}>{parse(value)}</div>;
        default:
          return null;
      }
    });

  return (
    <ModalUser title={t('Privacy Policy & Terms and Conditions')} show={show} onClose={onClose}>
      {/* -------------------------------------------------- PP & TC testo */}
      <div className="h-96 overflow-y-auto flex flex-col gap-4">
        {isFetching ? (
          <div className="w-full h-full flex justify-center items-center">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {renderBlocks(pp?.data, pp?.updated_at, 'pp')}
            {renderBlocks(tc?.data, tc?.updated_at, 'tc')}
          </>
        )}
      </div>

      {/* -------------------------------------------------- Checkbox marketing */}
      {!isFetching && (
        <div className="flex items-center gap-2 mt-6">
          <Checkbox
            checked={marketingAccepted}
            onChange={handleMarketingChange}
            disabled={savingMarketing || isLoading || isFetchingMarketing}
          />
          <span className="text-xs font-normal text-[#828282] mt-2">{t('Signup.AcceptMarketing')}</span>
          {(savingMarketing || isFetchingMarketing) && <Spin size="small" className="ml-2" />}
        </div>
      )}

      {/* -------------------------------------------------- CTA bottoni */}
      <div className="flex justify-end items-center gap-4 mt-4">
        <CustomButton buttonColor="secondary" disabled={isLoading} onClick={onLogout}>
          {t('Logout')}
        </CustomButton>
        <CustomButton onClick={!isLoading ? onSubmit : () => {}} isLoading={isLoading}>
          {t('Accept')}
        </CustomButton>
      </div>
    </ModalUser>
  );
}
